import { ChangeDetectionStrategy, Component, effect, input, linkedSignal, output } from '@angular/core';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { ButtonIconOnlyComponent } from '../button/button-icon-only.component';
import { InputCheckboxComponent } from '../input-checkbox/input-checkbox.component';

@Component({
  selector: 'rcp-option-list-group',
  template: `
    @if (expandable() || selectable() || label()) {
      <div
        [class.clickable]="expandable() || selectable()"
        class="option-group-header"
        (click)="expandable() && expanded.set(!expanded())"
      >
        <div class="flex justify-space-between">
          <div class="flex flex-gap align-center">
            @if (selectable()) {
              <rcp-input-checkbox
                [indeterminate]="!selected() && indeterminate()"
                [value]="selected()"
                [disabled]="disabled()"
                (select)="selectAll.emit($event)"
              />
              <span class="text max-2-lines"> {{ label() }}</span>
            } @else {
              <span class="text max-2-lines"> {{ label() }}</span>
            }
          </div>

          @if (expandable()) {
            <rcp-button-icon-only
              [icon]="expanded() ? faChevronUp : faChevronDown"
              (click)="$event.stopImmediatePropagation(); expanded.set(!expanded())"
            />
          }
        </div>
      </div>
    }

    <div class="group-content" [class.expanded]="expanded()">
      <ng-content></ng-content>
    </div>
  `,
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      gap: var(--item-internal-spacing-sm);

      /* only show the group separators when they are root */
      &.is-root::after {
        content: '';
        width: calc(100% - calc(var(--item-padding-md) * 2));
        border-bottom: 1px solid var(--col-lines);
        margin-left: var(--item-padding-md);
        font-weight: var(--font-weight-normal);

        /* with border bottom, this is ABOVE the line */
        padding-bottom: var(--item-padding-md);

        /* with border bottom, this is BELOW the line */
        margin-bottom: var(--item-padding-md);
      }
    }

    .option-group-header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      padding: 0 var(--item-padding-md);
      height: var(--item-height-md);
      font-weight: var(--font-weight-semi-bold);
      border-radius: var(--br);

      &:hover:where(.clickable) {
        background-color: var(--col-active-bg);
        color: var(--col-active-text);
      }

      &:focus:where(.clickable) {
        border: 1px solid var(--col-active-lines);
        background-color: var(--col-active-bg);
        color: var(--col-active-text);
      }
    }

    .group-content {
      display: none;
    }

    .group-content.expanded {
      display: flex;
      flex-direction: column;
      gap: var(--item-internal-spacing-sm);
    }
  `,
  imports: [InputCheckboxComponent, ButtonIconOnlyComponent],
  host: {
    '[class.is-root]': 'withDivider()',
  },
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class OptionListGroupComponent {
  label = input<string>('');

  selectable = input<boolean>(false);
  selected = input<boolean>(false);
  indeterminate = input<boolean>(false);
  startExpanded = input<boolean>(false);
  expandable = input<boolean>(true);

  withDivider = input<boolean>(false);

  disabled = input<boolean>(false);

  selectAll = output<boolean>();
  expandedChange = output<boolean>();

  // if not expandable, we start expanded
  expanded = linkedSignal(() => this.startExpanded() || !this.expandable());

  constructor() {
    effect(() => this.expandedChange.emit(this.expanded()));
  }

  protected readonly faChevronUp = faChevronUp;
  protected readonly faChevronDown = faChevronDown;
}
